import { useState, useEffect, useCallback } from 'react';
import type { ToolbarShortcut } from '../components/form-controls/ShortcutableControl';

const STORAGE_KEY = 'qe-toolbar-shortcuts';

interface StoredShortcuts {
  [elementType: string]: ToolbarShortcut[];
}

export const useToolbarShortcuts = () => {
  const [shortcuts, setShortcuts] = useState<StoredShortcuts>({});

  // Load shortcuts from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setShortcuts(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load toolbar shortcuts:', error);
    }
  }, []);

  // Persist shortcuts to localStorage
  const persistShortcuts = useCallback((newShortcuts: StoredShortcuts) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newShortcuts));
    } catch (error) {
      console.error('Failed to save toolbar shortcuts:', error);
    }
  }, []);

  const addShortcut = useCallback((shortcutConfig: Omit<ToolbarShortcut, 'id'>): boolean => {
    const id = `shortcut-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newShortcut: ToolbarShortcut = { ...shortcutConfig, id };

    let wasAdded = false;

    setShortcuts((prev) => {
      const elementShortcuts = prev[shortcutConfig.elementType] || [];

      // Check if shortcut already exists for this property
      const exists = elementShortcuts.some(
        (s) => s.propertyPath === shortcutConfig.propertyPath
      );

      if (exists) {
        // Don't add duplicate
        return prev;
      }

      wasAdded = true;
      const updated: StoredShortcuts = {
        ...prev,
        [shortcutConfig.elementType]: [...elementShortcuts, newShortcut],
      };

      persistShortcuts(updated);
      return updated;
    });

    return wasAdded;
  }, [persistShortcuts]);

  const removeShortcut = useCallback((elementType: string, shortcutId: string) => {
    setShortcuts((prev) => {
      const elementShortcuts = prev[elementType] || [];
      const filtered = elementShortcuts.filter((s) => s.id !== shortcutId);

      if (filtered.length === elementShortcuts.length) {
        return prev;
      }

      const updated: StoredShortcuts = { ...prev };

      if (filtered.length > 0) {
        updated[elementType] = filtered;
      } else {
        delete updated[elementType];
      }

      persistShortcuts(updated);
      return updated;
    });
  }, [persistShortcuts]);

  const getShortcutsForElement = useCallback(
    (elementType: string): ToolbarShortcut[] => {
      return shortcuts[elementType] || [];
    },
    [shortcuts]
  );

  const clearAllShortcuts = useCallback(() => {
    setShortcuts(() => {
      const emptied: StoredShortcuts = {};
      persistShortcuts(emptied);
      return emptied;
    });
  }, [persistShortcuts]);

  return {
    shortcuts,
    addShortcut,
    removeShortcut,
    getShortcutsForElement,
    clearAllShortcuts,
  };
};
